// Modified SM-2.
//
//  - Vanilla SM-2 sends mastered cards out months/years; for a 7yo's working
//    memory that's catastrophic, so we cap the interval at 14 days.
//  - We accept a continuous quality 0–5 (mapped from speech score) instead of
//    the user-facing "easy/good/hard" — speech scoring is already continuous.
//  - On a miss, interval = 1 day (not 0) so the card resurfaces tomorrow
//    rather than spamming the same encounter.
//  - The "yellow hit" (right syllable, wrong tone) path stays inside scoring.ts;
//    here it presents as quality=3 (Glance) which does not reset the interval.

import { db, type SRSState } from '../data/db';
import type { Card } from '../content/cardSchema';

const MAX_INTERVAL_DAYS = 14;
const MIN_EASE = 1.3;
const INITIAL_EASE = 2.5;

// score 0–100 → SM-2 quality 0–5
export function scoreToQuality(score: number): number {
  if (score >= 90) return 5; // critical
  if (score >= 70) return 4; // hit
  if (score >= 50) return 3; // glance (yellow)
  if (score >= 30) return 2; // miss
  return 1;                  // fumble
}

export interface SrsUpdate {
  state: SRSState;
  wasNew: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export async function applyAttempt(
  profileId: string,
  cardId: string,
  score: number,
  now: number = Date.now(),
): Promise<SrsUpdate> {
  const quality = scoreToQuality(score);
  const existing = await db.srs.get({ profileId, cardId });
  const wasNew = !existing;

  const state: SRSState = existing ?? {
    profileId,
    cardId,
    ease: INITIAL_EASE,
    intervalDays: 0,
    dueAt: now,
    reps: 0,
    lapses: 0,
    lastAttemptAccuracy: 0,
    lastSeenAt: now,
  };

  state.lastAttemptAccuracy = score;
  state.lastSeenAt = now;

  if (quality < 3) {
    // Miss or fumble — back to learning, but never zero days (spammy).
    state.lapses += 1;
    state.reps = 0;
    state.intervalDays = 1;
  } else {
    // Pass-ish. SM-2 ease update.
    state.ease = Math.max(
      MIN_EASE,
      state.ease + 0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02),
    );
    state.reps += 1;
    if (state.reps === 1) state.intervalDays = 1;
    else if (state.reps === 2) state.intervalDays = 3;
    else state.intervalDays = Math.min(MAX_INTERVAL_DAYS, Math.round(state.intervalDays * state.ease));
  }

  state.dueAt = now + state.intervalDays * DAY_MS;

  if (existing) await db.srs.update(existing.id!, state);
  else await db.srs.add(state);

  return { state, wasNew };
}

// Encounter draw: the "5 + 3 + 2" rule from the pedagogy memo.
//
//   5 due-review : cards from this lesson whose dueAt <= now, weighted by leech score
//   3 recent     : seen in the last 48h but not yet mastered (interval < 7)
//   2 new        : never-seen cards
//
// For ages 4–5 we drop to a 3+2+1 mix (six total).
export interface DrawOptions {
  profileId: string;
  lessonId: string;            // current zone's lesson
  size?: '10' | '6';           // 10 for ages 6+, 6 for ages 4–5
  now?: number;
}

export async function drawEncounter(opts: DrawOptions): Promise<Card[]> {
  const now = opts.now ?? Date.now();
  const size = opts.size ?? '10';
  const targets = size === '6' ? { due: 3, recent: 2, fresh: 1 } : { due: 5, recent: 3, fresh: 2 };

  const cards = await db.cards.where('lessonId').equals(opts.lessonId).toArray();
  if (cards.length === 0) return [];

  const srsList = await db.srs.where('profileId').equals(opts.profileId).toArray();
  const srsByCard = new Map(srsList.map((s) => [s.cardId, s] as const));

  const due: Array<{ card: Card; weight: number }> = [];
  const recent: Array<{ card: Card; weight: number }> = [];
  const fresh: Card[] = [];

  for (const card of cards) {
    const s = srsByCard.get(card.id);
    if (!s) { fresh.push(card); continue; }
    const leechWeight = 1 + Math.min(3, s.lapses) * 0.7; // capped boost
    if (s.dueAt <= now) due.push({ card, weight: leechWeight });
    else if (now - s.lastSeenAt < 48 * 60 * 60 * 1000 && s.intervalDays < 7) {
      recent.push({ card, weight: leechWeight });
    }
  }

  const out: Card[] = [
    ...weightedSample(due, targets.due),
    ...weightedSample(recent, targets.recent),
    ...shuffle(fresh).slice(0, targets.fresh),
  ];

  // If any bucket short, top up from the others so we always return ~target size.
  const target = targets.due + targets.recent + targets.fresh;
  if (out.length < target) {
    const seen = new Set(out.map((c) => c.id));
    const pool = shuffle(cards.filter((c) => !seen.has(c.id)));
    out.push(...pool.slice(0, target - out.length));
  }
  return out;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function weightedSample(pool: Array<{ card: Card; weight: number }>, n: number): Card[] {
  const remaining = [...pool];
  const out: Card[] = [];
  while (remaining.length && out.length < n) {
    const total = remaining.reduce((s, x) => s + x.weight, 0);
    let r = Math.random() * total;
    let pickedIdx = 0;
    for (let i = 0; i < remaining.length; i++) {
      r -= remaining[i].weight;
      if (r <= 0) { pickedIdx = i; break; }
    }
    out.push(remaining[pickedIdx].card);
    remaining.splice(pickedIdx, 1);
  }
  return out;
}

// Zone-unlock gate — concrete definition from the plan:
// ≥ 70% of lesson cards must have intervalDays ≥ 3 AND lastAttemptAccuracy ≥ 70.
export async function isLessonMastered(profileId: string, lessonId: string): Promise<boolean> {
  const cards = await db.cards.where('lessonId').equals(lessonId).toArray();
  if (cards.length === 0) return false;
  const srsList = await db.srs.where('profileId').equals(profileId).toArray();
  const srsById = new Map(srsList.map((s) => [s.cardId, s] as const));
  const mastered = cards.filter((c) => {
    const s = srsById.get(c.id);
    return s && s.intervalDays >= 3 && s.lastAttemptAccuracy >= 70;
  });
  return mastered.length / cards.length >= 0.7;
}
